/**
 * Paid Advertising Service
 * Handles Google Ads, Meta Ads, performance optimization, and bid automation
 */
import api from './api';

const paidAdsService = {
  // ==================== Status ====================
  /**
   * Get paid ads service status
   */
  getStatus: async () => {
    const response = await api.get('/marketing/paid-ads/status');
    return response.data;
  },

  // ==================== Google Ads ====================
  /**
   * Create Google Ads campaign
   */
  createGoogleCampaign: async (campaignConfig) => {
    const response = await api.post('/marketing/paid-ads/google-campaign', campaignConfig);
    return response.data;
  },

  /**
   * Get Google Ads campaigns
   */
  getGoogleCampaigns: async (params = {}) => {
    const response = await api.get('/marketing/paid-ads/google-campaigns', { params });
    return response.data;
  },

  /**
   * Update Google Ads campaign
   */
  updateGoogleCampaign: async (campaignId, campaignData) => {
    const response = await api.put(`/marketing/paid-ads/google-campaigns/${campaignId}`, campaignData);
    return response.data;
  },

  /**
   * Pause Google Ads campaign
   */
  pauseGoogleCampaign: async (campaignId) => {
    const response = await api.post(`/marketing/paid-ads/google-campaigns/${campaignId}/pause`);
    return response.data;
  },

  /**
   * Resume Google Ads campaign
   */
  resumeGoogleCampaign: async (campaignId) => {
    const response = await api.post(`/marketing/paid-ads/google-campaigns/${campaignId}/resume`);
    return response.data;
  },

  // ==================== Meta Ads ====================
  /**
   * Create Meta (Facebook/Instagram) Ads campaign
   */
  createMetaCampaign: async (campaignConfig) => {
    const response = await api.post('/marketing/paid-ads/meta-campaign', campaignConfig);
    return response.data;
  },

  /**
   * Get Meta Ads campaigns
   */
  getMetaCampaigns: async (params = {}) => {
    const response = await api.get('/marketing/paid-ads/meta-campaigns', { params });
    return response.data;
  },

  /**
   * Update Meta Ads campaign
   */
  updateMetaCampaign: async (campaignId, campaignData) => {
    const response = await api.put(`/marketing/paid-ads/meta-campaigns/${campaignId}`, campaignData);
    return response.data;
  },

  /**
   * Pause Meta Ads campaign
   */
  pauseMetaCampaign: async (campaignId) => {
    const response = await api.post(`/marketing/paid-ads/meta-campaigns/${campaignId}/pause`);
    return response.data;
  },

  /**
   * Resume Meta Ads campaign
   */
  resumeMetaCampaign: async (campaignId) => {
    const response = await api.post(`/marketing/paid-ads/meta-campaigns/${campaignId}/resume`);
    return response.data;
  },

  // ==================== Performance Optimization ====================
  /**
   * Optimize campaign performance
   */
  optimizePerformance: async (optimizationConfig) => {
    const response = await api.post('/marketing/paid-ads/optimize-performance', optimizationConfig);
    return response.data;
  },

  /**
   * Get optimization recommendations
   */
  getRecommendations: async (campaignId) => {
    const response = await api.get(`/marketing/paid-ads/campaigns/${campaignId}/recommendations`);
    return response.data;
  },

  /**
   * Apply optimization recommendation
   */
  applyRecommendation: async (campaignId, recommendationId) => {
    const response = await api.post(`/marketing/paid-ads/campaigns/${campaignId}/recommendations/${recommendationId}/apply`);
    return response.data;
  },

  // ==================== Bid Automation ====================
  /**
   * Setup bid automation
   */
  setupBidAutomation: async (automationConfig) => {
    const response = await api.post('/marketing/paid-ads/automate-bidding', automationConfig);
    return response.data;
  },

  /**
   * Get bid automation status
   */
  getBidAutomationStatus: async (campaignId) => {
    const response = await api.get(`/marketing/paid-ads/campaigns/${campaignId}/bid-automation`);
    return response.data;
  },

  /**
   * Update bid automation settings
   */
  updateBidAutomation: async (campaignId, settings) => {
    const response = await api.put(`/marketing/paid-ads/campaigns/${campaignId}/bid-automation`, settings);
    return response.data;
  },

  /**
   * Disable bid automation
   */
  disableBidAutomation: async (campaignId) => {
    const response = await api.delete(`/marketing/paid-ads/campaigns/${campaignId}/bid-automation`);
    return response.data;
  },

  // ==================== Performance Tracking ====================
  /**
   * Track campaign performance
   */
  trackPerformance: async (trackingConfig) => {
    const response = await api.post('/marketing/paid-ads/performance-tracking', trackingConfig);
    return response.data;
  },

  /**
   * Get performance metrics
   */
  getPerformanceMetrics: async (campaignId, params = {}) => {
    const response = await api.get(`/marketing/paid-ads/campaigns/${campaignId}/metrics`, { params });
    return response.data;
  },

  /**
   * Get performance report
   */
  getPerformanceReport: async (params = {}) => {
    const response = await api.get('/marketing/paid-ads/reports/performance', { params });
    return response.data;
  },

  /**
   * Export performance report
   */
  exportPerformanceReport: async (params = {}) => {
    const response = await api.get('/marketing/paid-ads/reports/export', {
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // ==================== Budgets ====================
  /**
   * Get budget overview
   */
  getBudgetOverview: async (params = {}) => {
    const response = await api.get('/marketing/paid-ads/budgets', { params });
    return response.data;
  },

  /**
   * Update campaign budget
   */
  updateBudget: async (campaignId, budgetData) => {
    const response = await api.put(`/marketing/paid-ads/campaigns/${campaignId}/budget`, budgetData);
    return response.data;
  },

  /**
   * Get budget recommendations
   */
  getBudgetRecommendations: async (campaignId) => {
    const response = await api.get(`/marketing/paid-ads/campaigns/${campaignId}/budget-recommendations`);
    return response.data;
  },

  // ==================== Audiences ====================
  /**
   * Get saved audiences
   */
  getAudiences: async (platform, params = {}) => {
    const response = await api.get(`/marketing/paid-ads/${platform}/audiences`, { params });
    return response.data;
  },

  /**
   * Create audience
   */
  createAudience: async (platform, audienceData) => {
    const response = await api.post(`/marketing/paid-ads/${platform}/audiences`, audienceData);
    return response.data;
  },

  /**
   * Get lookalike audience suggestions
   */
  getLookalikeAudiences: async (platform, sourceAudienceId) => {
    const response = await api.get(`/marketing/paid-ads/${platform}/audiences/${sourceAudienceId}/lookalike`);
    return response.data;
  }
};

export default paidAdsService;
